import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { Eye, FileText, ShoppingBag, Search, Loader2, ExternalLink } from 'lucide-react';
import { OrderDetailDrawer } from './OrderDetailDrawer';
interface OrdersHistoryTableProps {
  orders: any[];
  orderItems: any[];
  restaurantId: string | number | undefined;
  currencySymbol?: string;
}
const EXCLUDED_STATUSES = new Set(['cancelled', 'canceled', 'refunded', 'rejected']);
const STATUS_LABEL: Record<string, string> = {
  new: 'Nuevo',
  pending: 'Pendiente',
  accepted: 'Aceptado',
  preparing: 'En preparación',
  ready: 'Listo',
  searching_driver: 'Buscando conductor',
  out_for_delivery: 'En camino',
  delivered: 'Entregado',
  paid: 'Pagado',
  closed: 'Cerrado',
  completed: 'Completado'
};
const STATUS_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  delivered: 'success',
  paid: 'success',
  closed: 'success',
  completed: 'success',
  ready: 'success',
  preparing: 'info',
  accepted: 'info',
  out_for_delivery: 'info',
  searching_driver: 'warning',
  new: 'warning',
  pending: 'warning'
};
const CHANNEL_LABEL: Record<string, string> = {
  web: 'Marketplace',
  qr: 'QR / Mesa',
  waiter: 'Mesero',
  pos: 'POS',
  whatsapp: 'WhatsApp'
};
export function OrdersHistoryTable({
  orders,
  orderItems,
  restaurantId,
  currencySymbol = '₡'
}: OrdersHistoryTableProps) {
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  // Index items by order_id for quick lookup
  const itemsByOrder = useMemo(() => {
    const map = new Map<string | number, any[]>();
    for (const item of orderItems) {
      const arr = map.get(item.order_id) || [];
      arr.push(item);
      map.set(item.order_id, arr);
    }
    return map;
  }, [orderItems]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter(o => !EXCLUDED_STATUSES.has(String(o.status || '').toLowerCase())).filter(o => {
      if (!term) return true;
      const hay = [o.order_number, o.customer_name, o.customer_phone, o.customer_email].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    }).sort((a, b) => a.created_at < b.created_at ? 1 : -1);
  }, [orders, search]);
  const totalRevenue = useMemo(() => filtered.reduce((sum, o) => sum + (Number(o.total) || 0), 0), [filtered]);
  const handleGenerateInvoice = async (orderId: string | number) => {
    setInvoiceLoadingId(String(orderId));
    setInvoiceError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('payments').select('id').eq('order_id', orderId).order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      if (error) throw error;
      if (!data?.id) {
        setInvoiceError('Este pedido aún no se ha cobrado en Caja. Cobra primero para generar la factura.');
        return;
      }
      window.open(`/factura/${data.id}`, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('[OrdersHistoryTable] invoice error:', err);
      setInvoiceError('No se pudo generar la factura. Intenta de nuevo.');
    } finally {
      setInvoiceLoadingId(null);
    }
  };
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <>
      <Card data-id="element-969">
        <CardHeader data-id="element-970">
          <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-971">
            <div data-id="element-972">
              <CardTitle className="flex items-center gap-2" data-id="element-973">
                <ShoppingBag className="w-5 h-5 text-brand-primary" data-id="element-974" />
                Histórico de Pedidos
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1" data-id="element-975">
                {filtered.length} pedidos · Total{' '}
                {formatCurrency(totalRevenue, currencySymbol)}{' '}
                <span className="text-gray-400" data-id="element-976">
                  (no incluye pedidos cancelados)
                </span>
              </p>
            </div>
            <div className="relative w-full sm:w-72" data-id="element-977">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-978" />
              <input type="text" placeholder="Buscar por pedido, cliente, teléfono..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" data-id="element-979" />
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-980">
          {invoiceError && <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start justify-between gap-3" data-id="element-981">
              <span data-id="element-982">{invoiceError}</span>
              <button onClick={() => setInvoiceError(null)} className="text-amber-500 hover:text-amber-700 font-bold" data-id="element-983">
                ✕
              </button>
            </div>}

          {filtered.length === 0 ? <div className="text-center py-8 text-gray-500 text-sm" data-id="element-984">
              {search ? 'No se encontraron pedidos con esa búsqueda.' : 'No hay pedidos en el rango seleccionado.'}
            </div> : <div className="overflow-x-auto -mx-6" data-id="element-985">
              <table className="w-full text-sm text-left min-w-[900px]" data-id="element-986">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-987">
                  <tr data-id="element-988">
                    <th className="px-6 py-3 rounded-l-lg" data-id="element-989">Pedido</th>
                    <th className="px-4 py-3" data-id="element-990">Fecha</th>
                    <th className="px-4 py-3" data-id="element-991">Cliente</th>
                    <th className="px-4 py-3" data-id="element-992">Canal</th>
                    <th className="px-4 py-3" data-id="element-993">Estado</th>
                    <th className="px-4 py-3" data-id="element-994">Items</th>
                    <th className="px-4 py-3 text-right" data-id="element-995">Total</th>
                    <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-996">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-997">
                  {filtered.map(o => {
                const items = itemsByOrder.get(o.id) || [];
                const itemCount = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
                const channel = CHANNEL_LABEL[String(o.source || '').toLowerCase()] || o.source || '—';
                const statusKey = String(o.status || '').toLowerCase();
                return <tr key={o.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-998">
                        <td className="px-6 py-3 font-mono text-xs text-gray-700" data-id="element-999">
                          {o.order_number || `#${o.id}`}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap" data-id="element-1000">
                          {formatDate(o.created_at)}
                        </td>
                        <td className="px-4 py-3" data-id="element-1001">
                          <div className="font-medium text-gray-900" data-id="element-1002">
                            {o.customer_name || 'Sin nombre'}
                          </div>
                          {o.customer_phone && <div className="text-xs text-gray-500" data-id="element-1003">
                              {o.customer_phone}
                            </div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600" data-id="element-1004">{channel}</td>
                        <td className="px-4 py-3" data-id="element-1005">
                          <Badge variant={STATUS_VARIANT[statusKey] || 'default'} data-id="element-1006">
                            {STATUS_LABEL[statusKey] || o.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600" data-id="element-1007">
                          {itemCount}{' '}
                          {itemCount === 1 ? 'producto' : 'productos'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap" data-id="element-1008">
                          {formatCurrency(o.total, currencySymbol)}
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-1009">
                          <div className="flex items-center justify-end gap-1" data-id="element-1010">
                            <button onClick={() => setSelectedOrderId(String(o.id))} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors" title="Ver detalle" data-id="element-1011">
                              <Eye className="w-3.5 h-3.5" data-id="element-1012" />
                              Detalle
                            </button>
                            <button onClick={() => handleGenerateInvoice(o.id)} disabled={invoiceLoadingId === String(o.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-brand-primary hover:bg-brand-primary/10 transition-colors disabled:opacity-50" title="Generar factura" data-id="element-1013">
                              {invoiceLoadingId === String(o.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-1014" /> : <FileText className="w-3.5 h-3.5" data-id="element-1015" />}
                              Factura
                              <ExternalLink className="w-3 h-3 opacity-60" data-id="element-1016" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </CardContent>
      </Card>

      <OrderDetailDrawer isOpen={!!selectedOrderId} onClose={() => setSelectedOrderId(null)} orderId={selectedOrderId} data-id="element-1017" />
    </>;
}